import { Link } from 'react-router-dom';
import { BadgeCheck, Heart, Star, MapPin, MessageCircle } from 'lucide-react';
import { useWishlist } from '../lib/useWishlist';
import { waLink, cleanImageUrl, cleanImageList } from '../lib/constants';
import { ikThumb } from '../lib/imagekit';

export const ArtistCard = ({ artist }) => {
  const { has, toggle } = useWishlist();
  const liked = has(artist.slug);
  // On the home featured grid we want to show the admin-chosen hero image (falls back to profile image).
  // Ignore placeholder/empty featured images so stale picsum placeholders never win over the real photo.
  const heroImage = cleanImageUrl(artist.featured_image) || artist.profile_image;
  const thumbs = cleanImageList(artist.portfolio).slice(0, 3);

  return (
    <article
      data-testid={`artist-card-${artist.slug}`}
      className="group bg-white border border-transparent hover:border-[#eaeaea] transition-colors"
    >
      <div className="relative img-zoom aspect-[4/5] bg-[#f7f7f7]">
        <Link to={`/artists/${artist.slug}`} aria-label={`View ${artist.name}`}>
          <img src={ikThumb(heroImage, 800)} alt={artist.name} className="w-full h-full object-cover" loading="lazy" />
        </Link>

        {artist.verified && (
          <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur px-2.5 py-1 text-[10px] uppercase tracking-[0.2em]">
            <BadgeCheck className="w-3.5 h-3.5 text-[#eb5381]" />
            Verified
          </div>
        )}

        <button
          onClick={() => toggle(artist.slug)}
          data-testid={`wishlist-toggle-${artist.slug}`}
          aria-label="Wishlist"
          className={`absolute top-4 right-4 w-9 h-9 grid place-items-center bg-white/95 backdrop-blur transition-colors ${
            liked ? 'text-[#eb5381]' : 'text-neutral-700 hover:text-[#eb5381]'
          }`}
        >
          <Heart className={`w-4 h-4 ${liked ? 'fill-[#eb5381]' : ''}`} />
        </button>

        {/* Mini portfolio strip */}
        <div className="absolute -bottom-px left-0 right-0 flex">
          {thumbs.map((src, i) => (
            <div key={i} className="flex-1 aspect-square border-l first:border-l-0 border-white">
              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </div>
      </div>

      <div className="pt-5 pb-2 px-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <Link to={`/artists/${artist.slug}`} className="font-serif text-2xl leading-tight tracking-tight hover:text-[#eb5381] transition-colors">
              {artist.name}
            </Link>
            <div className="mt-1 flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-neutral-500">
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{artist.city}</span>
              <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 fill-[#eb5381] text-[#eb5381]" />{artist.glam_score}</span>
              <span>{(artist.followers / 1000).toFixed(1)}k</span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">From</div>
            <div className="font-serif text-xl">₹{artist.starting_price.toLocaleString('en-IN')}</div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {artist.specializations.slice(0, 3).map(s => (
            <span key={s} className="text-[10px] uppercase tracking-[0.2em] bg-[#ffefd1] px-2.5 py-1">
              {s}
            </span>
          ))}
        </div>

        <div className="mt-5 flex gap-2">
          <Link
            to={`/artists/${artist.slug}`}
            data-testid={`view-profile-${artist.slug}`}
            className="flex-1 text-center bg-[#eb5381] hover:bg-[#d64570] text-white px-4 py-3 text-[11px] uppercase tracking-[0.2em] transition-colors"
          >
            View Profile
          </Link>
          <a
            href={waLink(`Hi! I'd like to book ${artist.name} from ${artist.city}.`)}
            target="_blank"
            rel="noreferrer"
            data-testid={`whatsapp-book-${artist.slug}`}
            className="w-12 grid place-items-center border border-black hover:bg-black hover:text-white transition-colors"
            aria-label="Book on WhatsApp"
          >
            <MessageCircle className="w-4 h-4" />
          </a>
        </div>
      </div>
    </article>
  );
};
